import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { mfgCatalogApi, mfgCatalogKeys, useMfgItemPrices, type MfgItemDetail, type MfgItemPrice } from "@/lib/mfg-catalog";
import { ApiError } from "@/lib/api";

const money = (n: number) => new Intl.NumberFormat("en-AU", { style: "currency", currency: "AUD" }).format(n);
const dateOnly = (iso: string) => iso.slice(0, 10);

export function PriceBreaksSection({ item }: { item: MfgItemDetail }) {
  const qc = useQueryClient();
  const { data: prices } = useMfgItemPrices(item.mfgItemId);
  const invalidate = () => { void qc.invalidateQueries({ queryKey: mfgCatalogKeys.prices(item.mfgItemId) }); void qc.invalidateQueries({ queryKey: mfgCatalogKeys.detail(item.mfgItemId) }); };
  const on409 = (e: unknown) => toast.error(e instanceof ApiError && e.status === 409 ? "Changed since you opened it — reload." : `Failed: ${(e as Error).message}`);

  const [cost, setCost] = useState("");
  const [minQty, setMinQty] = useState("1");
  const [start, setStart] = useState(new Date().toISOString().slice(0, 10));
  const [end, setEnd] = useState("");

  const add = useMutation({
    mutationFn: () => mfgCatalogApi.createPrice(item.mfgItemId, { costPrice: Number(cost), minimumQuantity: Number(minQty), startDate: new Date(start).toISOString(), endDate: end ? new Date(end).toISOString() : null }),
    onSuccess: () => { invalidate(); setCost(""); setMinQty("1"); toast.success("Price added"); },
    onError: on409,
  });
  const del = useMutation({
    mutationFn: (p: MfgItemPrice) => mfgCatalogApi.removePrice(p.mfgItemPriceId, p.rowVersionBase64),
    onSuccess: () => { invalidate(); toast.success("Removed"); },
    onError: on409,
  });

  return (
    <section className="space-y-3">
      <h3 className="text-sm font-semibold text-muted-foreground">Price breaks</h3>
      <table className="w-full text-sm">
        <thead className="text-muted-foreground"><tr><th className="text-left">Cost</th><th className="text-left">Min qty</th><th className="text-left">From</th><th className="text-left">To</th><th></th></tr></thead>
        <tbody>
          {(prices ?? []).map((p) => (
            <tr key={p.mfgItemPriceId} className="border-t">
              <td>{money(p.costPrice)}</td><td>{p.minimumQuantity}</td><td>{dateOnly(p.startDate)}</td><td>{p.endDate ? dateOnly(p.endDate) : "—"}</td>
              <td className="text-right"><Button variant="ghost" size="sm" onClick={() => del.mutate(p)}>Remove</Button></td>
            </tr>
          ))}
          {(prices ?? []).length === 0 && <tr><td colSpan={5} className="py-2 text-muted-foreground">No price breaks yet.</td></tr>}
        </tbody>
      </table>
      <div className="flex items-end gap-2">
        <div><span className="text-xs">Cost</span><Input type="number" value={cost} onChange={(e) => setCost(e.target.value)} className="w-28" /></div>
        <div><span className="text-xs">Min qty</span><Input type="number" value={minQty} onChange={(e) => setMinQty(e.target.value)} className="w-24" /></div>
        <div><span className="text-xs">From</span><Input type="date" value={start} onChange={(e) => setStart(e.target.value)} /></div>
        <div><span className="text-xs">To</span><Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} /></div>
        <Button onClick={() => add.mutate()} disabled={add.isPending || cost.trim() === ""}>Add break</Button>
      </div>
    </section>
  );
}
